import {
    memo,
    type SyntheticEvent,
    type PropsWithChildren
} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getFormInputProps } from "util/forms";
import { getOptionName } from "util/option";
import { selectCurrencies } from "features/general/selectors";
import Autocomplete from "ui/atoms/Autocomplete";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, type BankingSDKRecipientProps } from "./types";

const BankingSDKRecipient = ({
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isBootstrapDataLoading,
    bootstrapData,
    children,
    ...restProps
}: PropsWithChildren<BankingSDKRecipientProps>) => {
    const isNew = !('pki' in values);

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    alignItems: 'flex-start',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)'
                    },
                    gap: {
                        xs: 2,
                        sm: 3
                    }
                }}
            >
                <Autocomplete
                    fullWidth
                    size='small'
                    layout={null}
                    loading={isBootstrapDataLoading}
                    disabled={isBootstrapDataLoading || !isNew}
                    options={bootstrapData.BankingSdkRecipientEndToEndSlug}
                    value={values[FormField.EndToEndIdSlug] || null}
                    onChange={onAutocompleteChange(FormField.EndToEndIdSlug)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.EndToEndIdSlug, restProps),
                        name: FormField.EndToEndIdSlug,
                        label: 'End-to-End ID Slug'
                    }}
                />
                <TextField
                    {...getFormInputProps(FormField.Name, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.Name}
                    value={values[FormField.Name]}
                    label='Name'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <StatefulSelector
                    disableClearable
                    selectOptions={selectCurrencies}
                    size='small'
                    value={values[FormField.AccountCurrency]}
                    TextFieldProps={{
                        label: 'Account Currency',
                        required: true
                    }}
                    onChange={(_, currency) => {
                        setFieldTouched(FormField.AccountCurrency, true);
                        setFieldValue(
                            FormField.AccountCurrency,
                            currency,
                            true
                        );
                    }}
                    FormControlProps={{
                        sx: {
                            width: '100%'
                        }
                    }}
                />
                <Autocomplete
                    fullWidth
                    disableClearable
                    size='small'
                    layout={null}
                    loading={isBootstrapDataLoading}
                    disabled={isBootstrapDataLoading}
                    options={bootstrapData.addressType}
                    getOptionLabel={getOptionName}
                    value={values[FormField.AddressType]}
                    onChange={onAutocompleteChange(FormField.AddressType)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.AddressType, restProps),
                        name: FormField.AddressType,
                        required: true,
                        label: 'Postal Address type'
                    }}
                />
                <TextField
                    {...getFormInputProps(FormField.Department, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.Department}
                    value={values[FormField.Department]}
                    label='Postal Address department'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.SubDepartment, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.SubDepartment}
                    value={values[FormField.SubDepartment]}
                    label='Postal Address sub-department'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.StreetName, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.StreetName}
                    value={values[FormField.StreetName]}
                    label='Postal Address street'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.BuildingNumber, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.BuildingNumber}
                    value={values[FormField.BuildingNumber]}
                    label='Postal Address building number'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.PostCode, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.PostCode}
                    value={values[FormField.PostCode]}
                    label='Postal Address postcode'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.TownName, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.TownName}
                    value={values[FormField.TownName]}
                    label='Postal Address town'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <CountryIso2
                    fullWidth
                    disableClearable
                    value={values[FormField.Country]}
                    onChange={onAutocompleteChange(FormField.Country)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.Country, restProps),
                        required: true,
                        name: FormField.Country,
                        label: 'Postal Address country'
                    }}
                />
                <TextField
                    {...getFormInputProps(
                        FormField.CountrySubdivision,
                        restProps
                    )}
                    fullWidth
                    required
                    size='small'
                    name={FormField.CountrySubdivision}
                    value={values[FormField.CountrySubdivision]}
                    label='Postal Address subdivision'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.IbanAccount, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.IbanAccount}
                    value={values[FormField.IbanAccount]}
                    label='IBAN'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.BbanAccount, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.BbanAccount}
                    value={values[FormField.BbanAccount]}
                    label='BBAN'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(FormField.AgentBicFi, restProps)}
                    fullWidth
                    required
                    size='small'
                    name={FormField.AgentBicFi}
                    value={values[FormField.AgentBicFi]}
                    label='Agent BIC FI'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <TextField
                    {...getFormInputProps(
                        FormField.ClearingMemberId,
                        restProps
                    )}
                    fullWidth
                    size='small'
                    name={FormField.ClearingMemberId}
                    value={values[FormField.ClearingMemberId]}
                    label='Clearing Member ID'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </Box>
            {children}
        </Box>
    );
};

export default memo(BankingSDKRecipient);
